import {useEffect, useMemo, useState} from "react";
import {
    EBirdChecklistByFamily,
    EBirdMyDataSchema,
    EBirdObservationsByLocation,
    EBirdObservationsBySpecies,
    getChecklistByFamily,
    getFilteredObservations,
    getObservationsByLocation,
    getObservationsBySpecies
} from "ebird-mydata-reader";
import checklistWithImages from '../assets/data/checklist-with-images.json';
import * as localforage from "localforage";
import {
    CachedLocationEcoData,
    CachedDerivedBirdData,
    DERIVED_BIRD_DATA_CACHE_KEY,
    DERIVED_BIRD_DATA_CACHE_VERSION,
    LOCATION_ECO_DATA_CACHE_KEY,
    LOCATION_ECO_DATA_CACHE_VERSION
} from "./birdDataCache";
import type {EnrichedObservationsByLocation, LocationEcoregionData} from "./types";
import {
    buildHotspotExplorerIndex
} from "../views/hotspot-explorer/helpers";
import type {HotspotExplorerIndex} from "../views/hotspot-explorer/types";

const checklistBaseWithImages = checklistWithImages as unknown as EBirdChecklistByFamily[];

export function useBirdData(rawData: any[]) {
    const [observationsBySpecies, setObservationsBySpecies] = useState<EBirdObservationsBySpecies[]>([]);
    const [observationsByLocation, setObservationsByLocation] = useState<EBirdObservationsByLocation[]>([]);
    const [ecoDataByLocationId, setEcoDataByLocationId] = useState<Record<string, LocationEcoregionData>>({});
    const [checklist, setChecklist] = useState<EBirdChecklistByFamily[]>([]);
    const lifeList = useMemo((): EBirdMyDataSchema[] => {
        return rawData.length ? getFilteredObservations(rawData, 'life') : [];
    }, [rawData]);

    useEffect(() => {
        let isActive = true;

        const resolveLocationEcoData = async () => {
            if (!rawData.length) {
                setEcoDataByLocationId({});
                return;
            }

            try {
                const cachedEcoData: CachedLocationEcoData | null = await localforage.getItem(LOCATION_ECO_DATA_CACHE_KEY);

                if (!isActive) return;

                setEcoDataByLocationId(
                    cachedEcoData?.version === LOCATION_ECO_DATA_CACHE_VERSION
                        ? cachedEcoData.ecoDataByLocationId ?? {}
                        : {}
                );
            } catch (error) {
                console.error(error);
                if (!isActive) return;

                setEcoDataByLocationId({});
            }
        }

        void resolveLocationEcoData();

        return () => {
            isActive = false;
        }
    }, [rawData]);

    useEffect(() => {
        let isActive = true;

        const resolveDerivedBirdData = async () => {
            if (!rawData.length) {
                setObservationsBySpecies([]);
                setObservationsByLocation([]);
                setChecklist([]);
                return;
            }

            try {
                const cachedData: CachedDerivedBirdData | null = await localforage.getItem(DERIVED_BIRD_DATA_CACHE_KEY);

                if (
                    cachedData?.version === DERIVED_BIRD_DATA_CACHE_VERSION
                    && cachedData.observationsBySpecies?.length
                    && cachedData.observationsByLocation?.length
                    && cachedData.checklist?.length
                ) {
                    if (!isActive) return;

                    setObservationsBySpecies(cachedData.observationsBySpecies);
                    setObservationsByLocation(cachedData.observationsByLocation);
                    setChecklist(cachedData.checklist);
                    return;
                }

                const nextObservationsBySpecies = getObservationsBySpecies(rawData);
                const nextObservationsByLocation = getObservationsByLocation(rawData);
                const nextChecklist = nextObservationsBySpecies.length
                    ? getChecklistByFamily(nextObservationsBySpecies, checklistBaseWithImages)
                    : [];

                await localforage.setItem(DERIVED_BIRD_DATA_CACHE_KEY, {
                    version: DERIVED_BIRD_DATA_CACHE_VERSION,
                    observationsBySpecies: nextObservationsBySpecies,
                    observationsByLocation: nextObservationsByLocation,
                    checklist: nextChecklist
                });

                if (!isActive) return;

                setObservationsBySpecies(nextObservationsBySpecies);
                setObservationsByLocation(nextObservationsByLocation);
                setChecklist(nextChecklist);
            } catch (error) {
                console.error(error);

                const nextObservationsBySpecies = getObservationsBySpecies(rawData);
                const nextObservationsByLocation = getObservationsByLocation(rawData);
                const nextChecklist = nextObservationsBySpecies.length
                    ? getChecklistByFamily(nextObservationsBySpecies, checklistBaseWithImages)
                    : [];

                if (!isActive) return;

                setObservationsBySpecies(nextObservationsBySpecies);
                setObservationsByLocation(nextObservationsByLocation);
                setChecklist(nextChecklist);
            }
        }

        void resolveDerivedBirdData();

        return () => {
            isActive = false;
        }
    }, [rawData]);

    const activeObservationsByLocation = useMemo((): EBirdObservationsByLocation[] | EnrichedObservationsByLocation[] => {
        if (!Object.keys(ecoDataByLocationId).length) {
            return observationsByLocation;
        }

        return observationsByLocation.map(locationGroup => {
            const ecoData = ecoDataByLocationId[locationGroup.locationId];
            if (!ecoData) return locationGroup;

            return {
                ...locationGroup,
                ecoData
            };
        });
    }, [ecoDataByLocationId, observationsByLocation]);
    const hotspotExplorerIndex = useMemo<HotspotExplorerIndex>(() => {
        return buildHotspotExplorerIndex(activeObservationsByLocation);
    }, [activeObservationsByLocation]);

    return {
        lifeList,
        observationsBySpecies,
        observationsByLocation: activeObservationsByLocation,
        checklist,
        hotspotExplorerIndex
    }
}
